import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useCreateAssociatedParty,
  useAssignAssociatedPartyToProduct,
  useListAssociatedParties,
  useUnassignAssociatedPartyFromProduct,
} from '@/shared/hooks/api/associatedParties'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AssociatedPartyForm } from '@/features/associated-parties/AssociatedPartyForm'
import { formatPartyCityCountry } from '@/features/associated-parties/formatAddress'
import { useCountryDisplayName } from '@/features/associated-parties/useCountryDisplayName'
import { useCreateAndAssignManufacturer } from '@/features/product-catalog/hooks/useCreateAndAssignManufacturer'

const MANUFACTURER_ROLE = 'MANUFACTURER' as const
const REMOVE_MANUFACTURER_VALUE = '__remove_manufacturer__'
const NO_ASSOCIATED_PARTIES_VALUE = '__no_associated_parties__'
const CREATE_MODE = 'create'
const SELECT_MODE = 'select'

type ProductManufacturerSelectDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  currentManufacturerId?: string | null
}

export function ProductManufacturerSelectDialog({
  open,
  onOpenChange,
  productId,
  currentManufacturerId,
}: ProductManufacturerSelectDialogProps) {
  const { t } = useTranslation('productCatalog')
  const getCountryName = useCountryDisplayName()
  const { data: orgParties = [], isLoading: isLoadingOrg } =
    useListAssociatedParties({ enabled: open })
  const createMutation = useCreateAssociatedParty()
  const assignMutation = useAssignAssociatedPartyToProduct(productId)
  const unassignMutation = useUnassignAssociatedPartyFromProduct(productId)
  const { createAndAssignManufacturer } = useCreateAndAssignManufacturer({
    currentManufacturerId,
    createAssociatedParty: createMutation.mutateAsync,
    unassignAssociatedPartyFromProduct: unassignMutation.mutateAsync,
    assignAssociatedPartyToProduct: assignMutation.mutateAsync,
  })
  const [selectedPartyId, setSelectedPartyId] = useState(
    currentManufacturerId ?? ''
  )
  const [mode, setMode] = useState<typeof CREATE_MODE | typeof SELECT_MODE>(
    SELECT_MODE
  )

  const isSaving =
    assignMutation.isPending ||
    unassignMutation.isPending ||
    createMutation.isPending
  const selectedParty = useMemo(
    () => orgParties.find((party) => party.id === selectedPartyId) ?? null,
    [orgParties, selectedPartyId]
  )
  const isRemoveSelection = selectedPartyId === REMOVE_MANUFACTURER_VALUE
  const isCreateMode = mode === CREATE_MODE
  const isSelectMode = mode === SELECT_MODE
  const selectItems = useMemo(() => {
    const items: Array<{ value: string; label: string }> = []
    if (currentManufacturerId) {
      items.push({
        value: REMOVE_MANUFACTURER_VALUE,
        label: t('detail.manufacturer.dialog.removeOption'),
      })
    }
    for (const party of orgParties) {
      const location = formatPartyCityCountry(party, getCountryName)
      const label = [
        party.name,
        location,
        party.id === currentManufacturerId &&
          `(${t('detail.manufacturer.current')})`,
      ]
        .filter(Boolean)
        .join(' ')
      items.push({ value: party.id, label })
    }
    if (orgParties.length === 0) {
      items.push({
        value: NO_ASSOCIATED_PARTIES_VALUE,
        label: t('detail.manufacturer.dialog.empty'),
      })
    }
    return items
  }, [currentManufacturerId, getCountryName, orgParties, t])

  useEffect(() => {
    if (open) {
      setSelectedPartyId(currentManufacturerId ?? '')
      setMode(SELECT_MODE)
    }
  }, [currentManufacturerId, open])

  const handleConfirm = useCallback(async () => {
    if (isSaving) {
      return
    }

    if (!selectedParty && !isRemoveSelection) {
      return
    }

    if (isRemoveSelection) {
      if (!currentManufacturerId) {
        onOpenChange(false)
        return
      }

      try {
        await unassignMutation.mutateAsync({
          associatedPartyId: currentManufacturerId,
          role: MANUFACTURER_ROLE,
        })
        toast.success(t('detail.manufacturer.removeSuccess'))
        onOpenChange(false)
      } catch {
        toast.error(t('detail.manufacturer.removeError'))
      }
      return
    }

    if (!selectedParty) {
      return
    }

    if (selectedParty.id === currentManufacturerId) {
      toast.success(t('detail.manufacturer.alreadyAssigned'))
      onOpenChange(false)
      return
    }

    try {
      if (currentManufacturerId) {
        await unassignMutation.mutateAsync({
          associatedPartyId: currentManufacturerId,
          role: MANUFACTURER_ROLE,
        })
      }

      await assignMutation.mutateAsync({
        associatedPartyId: selectedParty.id,
        role: MANUFACTURER_ROLE,
      })
      toast.success(t('detail.manufacturer.assignSuccess'))
      onOpenChange(false)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        toast.success(t('detail.manufacturer.alreadyAssigned'))
        onOpenChange(false)
        return
      }
      toast.error(t('detail.manufacturer.assignError'))
    }
  }, [
    assignMutation,
    currentManufacturerId,
    isRemoveSelection,
    isSaving,
    onOpenChange,
    selectedParty,
    t,
    unassignMutation,
  ])

  const handleCreateAndAssign = useCallback(
    async (payload: Parameters<typeof createMutation.mutateAsync>[0]) => {
      if (isSaving) {
        return
      }

      const result = await createAndAssignManufacturer(payload)
      if (result === 'success') {
        toast.success(t('detail.manufacturer.dialog.createAndAssignSuccess'))
        onOpenChange(false)
        return
      }
      if (result === 'alreadyAssigned') {
        toast.success(t('detail.manufacturer.alreadyAssigned'))
        onOpenChange(false)
        return
      }
      if (result === 'assignFailedAfterCreate') {
        toast.error(t('detail.manufacturer.dialog.assignNewError'))
        return
      }
      if (result === 'createFailed') {
        toast.error(t('detail.manufacturer.dialog.createAndAssignError'))
      }
    },
    [createAndAssignManufacturer, isSaving, onOpenChange, t]
  )

  let dialogTitle = t('detail.manufacturer.dialog.title')
  let dialogDescription = t('detail.manufacturer.dialog.description')

  if (isCreateMode) {
    dialogTitle = t('detail.manufacturer.dialog.createTitle')
    dialogDescription = t('detail.manufacturer.dialog.createDescription')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] max-w-4xl! flex-col gap-4'>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        {isLoadingOrg && isSelectMode ? (
          <p className='text-sm text-muted-foreground'>
            {t('detail.manufacturer.dialog.loading')}
          </p>
        ) : null}
        {isSelectMode && !isLoadingOrg ? (
          <>
            <Select
              value={selectedPartyId}
              onValueChange={(value) => setSelectedPartyId(value ?? '')}
              disabled={isSaving}
              items={selectItems}
            >
              <SelectTrigger className='w-full'>
                <SelectValue
                  placeholder={t('detail.manufacturer.dialog.placeholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {currentManufacturerId ? (
                  <SelectItem value={REMOVE_MANUFACTURER_VALUE}>
                    {t('detail.manufacturer.dialog.removeOption')}
                  </SelectItem>
                ) : null}
                {orgParties.length === 0 ? (
                  <SelectItem value={NO_ASSOCIATED_PARTIES_VALUE} disabled>
                    {t('detail.manufacturer.dialog.empty')}
                  </SelectItem>
                ) : (
                  orgParties.map((party) => {
                    const location = formatPartyCityCountry(party, getCountryName)
                    const label = [
                      party.name,
                      location,
                      party.id === currentManufacturerId &&
                        `(${t('detail.manufacturer.current')})`,
                    ]
                      .filter(Boolean)
                      .join(' ')
                    return (
                      <SelectItem key={party.id} value={party.id}>
                        {label}
                      </SelectItem>
                    )
                  })
                )}
              </SelectContent>
            </Select>
            <div className='text-sm text-muted-foreground'>
              {t('detail.manufacturer.dialog.createHintPrefix')}{' '}
              <Button
                type='button'
                variant='link'
                className='h-auto p-0 align-baseline text-sm'
                onClick={() => setMode(CREATE_MODE)}
                disabled={isSaving}
              >
                {t('detail.manufacturer.dialog.createCta')}
              </Button>{' '}
              {t('detail.manufacturer.dialog.createHintSuffix')}
            </div>
            {isRemoveSelection && (
              <p className='text-sm text-muted-foreground'>
                {t('detail.manufacturer.dialog.confirmRemoval')}
              </p>
            )}
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {t('detail.manufacturer.dialog.cancel')}
              </Button>
              <Button
                type='button'
                disabled={(!selectedParty && !isRemoveSelection) || isSaving}
                onClick={() => void handleConfirm()}
              >
                {isSaving ? (
                  <Loader2 className='size-4 animate-spin' aria-hidden />
                ) : null}
                {t('detail.manufacturer.dialog.confirm')}
              </Button>
            </DialogFooter>
          </>
        ) : null}
        {isCreateMode ? (
          <AssociatedPartyForm
            open={open}
            inputIdPrefix='product-manufacturer'
            onSubmit={handleCreateAndAssign}
            onCancel={() => setMode(SELECT_MODE)}
            submitLabel={t('detail.manufacturer.dialog.createSubmit')}
            cancelLabel={t('detail.manufacturer.dialog.backToSelect')}
            isPending={isSaving}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
